import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightLeft, FilePlus2, RefreshCw } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import SourcingRfpService from '../../services/sourcingRFPService';
import ContractService from '../../services/contractService';

const formatDateInput = (value) => {
  const dt = value instanceof Date ? value : new Date(value || Date.now());
  return dt.toISOString().split('T')[0];
};

const getBidAmount = (response) =>
  (response?.lineItems || []).reduce(
    (sum, item) => sum + (Number(item?.price) || 0),
    0,
  );

const extractArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const ContractConversion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [rfps, setRfps] = useState([]);
  const [responses, setResponses] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState('');
  const [selectedResponseId, setSelectedResponseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [lastLoadedRfpId, setLastLoadedRfpId] = useState('');
  const debounceTimerRef = useRef(null);
  const loadRequestIdRef = useRef(0);

  const preselectedResponse = location.state?.response || null;

  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  const loadData = useCallback(async (rfpId = '') => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);
    setLoadError('');
    try {
      const [rfpRes, responseRes] = await Promise.all([
        SourcingRfpService.getRfps(),
        SourcingRfpService.getResponses(rfpId || ''),
      ]);

      if (requestId !== loadRequestIdRef.current) return;

      const availableRfps = extractArray(rfpRes);
      const responseList = extractArray(responseRes);

      setRfps(availableRfps.filter((r) => r.status === 'AWARDED'));
      setResponses(responseList);
      setLastLoadedRfpId(rfpId || '');

      // Auto-select the awarded response when a specific RFP is chosen
      if (rfpId) {
        const awarded = responseList.find((r) => r.status === 'AWARDED');
        if (awarded) {
          setSelectedResponseId(awarded._id);
        }
      }
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      const message =
        err?.message ||
        err?.response?.data?.message ||
        'Unable to load sourcing data for conversion.';
      setLoadError(message);
      showErrorRef.current(message);
      console.error('[ContractConversion] sourcing load failed', {
        rfpId,
        message: err?.message,
        status: err?.status || err?.response?.status,
        data: err?.data || err?.response?.data,
      });
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadData('');
  }, [loadData]);

  useEffect(() => {
    if (!preselectedResponse?._id) return;

    const sourceRfpId =
      preselectedResponse.rfpId?._id || preselectedResponse.rfpId || '';
    if (sourceRfpId) {
      setSelectedRfpId(sourceRfpId);
      loadData(sourceRfpId);
    }
    setSelectedResponseId(preselectedResponse._id);
  }, [loadData, preselectedResponse]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      // Avoid duplicate request when the same RFP is already loaded.
      if ((selectedRfpId || '') !== (lastLoadedRfpId || '')) {
        loadData(selectedRfpId || '');
      }
    }, 350);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [selectedRfpId, lastLoadedRfpId, loadData]);


  const eligibleResponses = useMemo(
    () =>
      responses.filter((res) => ['AWARDED', 'EVALUATED'].includes(res.status)),
    [responses],
  );

  const selectedResponse = useMemo(
    () =>
      eligibleResponses.find((res) => res._id === selectedResponseId) || null,
    [eligibleResponses, selectedResponseId],
  );

  const mappingPreview = useMemo(() => {
    if (!selectedResponse) return null;

    const startDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);

    const amount = getBidAmount(selectedResponse);

    return {
      title: `${selectedResponse.rfpId?.title || 'Sourcing Contract'} - ${selectedResponse.vendorId?.name || 'Vendor'}`,
      type: 'Procurement',
      counterparty: selectedResponse.vendorId?.name || 'Unknown Vendor',
      startDate: formatDateInput(startDate),
      endDate: formatDateInput(endDate),
      status: 'Active',
      amount,
      currency: 'USD',
      paymentTerms: 'NET_30',
      frequency: 'MONTHLY',
      sourceRfpId: selectedResponse.rfpId?._id || selectedResponse.rfpId,
      sourceResponseId: selectedResponse._id,
    };
  }, [selectedResponse]);

  const handleCreateContract = async (retriedConfirm = false) => {
    if (!mappingPreview) {
      showError('Select a vendor response to create contract.');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        title: mappingPreview.title,
        type: mappingPreview.type,
        counterparty: mappingPreview.counterparty,
        startDate: mappingPreview.startDate,
        endDate: mappingPreview.endDate,
        status: mappingPreview.status,
        amount: Number(mappingPreview.amount || 0),
        currency: mappingPreview.currency,
        paymentTerms: mappingPreview.paymentTerms,
        frequency: mappingPreview.frequency,
        description: `Converted from sourcing response ${mappingPreview.sourceResponseId}`,
        sourceRfpId: mappingPreview.sourceRfpId,
        ...(retriedConfirm ? { confirmWarnings: true } : {}),
      };

      const createRes = await ContractService.createContract(payload);
      const createdId =
        createRes?.data?._id ||
        createRes?._id ||
        createRes?.contract?._id ||
        null;

      success('Contract created and pushed to CLM module.');

      if (createdId) {
        navigate(`/contracts/edit/${createdId}`);
      } else {
        navigate('/contracts/management');
      }
    } catch (err) {
      if (
        !retriedConfirm &&
        err?.response?.status === 409 &&
        err?.response?.data?.requireConfirmation
      ) {
        return handleCreateContract(true);
      }
      showError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create contract in CLM module.',
      );
    } finally {
      setCreating(false);
    }
  };


  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Contract Conversion Screen"
        description="Preview sourcing-to-contract mapping and push contract into CLM module"
        actions={[
          {
            icon: (
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            ),
            variant: 'outline',
            onClick: () => loadData(selectedRfpId),
          },
        ]}
      />

      <div className="page-container">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Sourcing Event
              </label>
              <select
                value={selectedRfpId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedRfpId(value);
                  setSelectedResponseId('');
                }}
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
              >
                <option value="">All Events</option>
                {rfps.map((rfp) => (
                  <option key={rfp._id} value={rfp._id}>
                    {rfp.rfpNumber} - {rfp.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Awarded/Evaluated Vendor Response
              </label>
              <select
                value={selectedResponseId}
                onChange={(e) => setSelectedResponseId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
              >
                <option value="">Select response</option>
                {eligibleResponses.map((res) => (
                  <option key={res._id} value={res._id}>
                    {res.vendorId?.name || 'Unknown Vendor'} -{' '}
                    {res.rfpId?.rfpNumber || 'No RFP'} ({res.status})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loadError && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="text-sm font-semibold text-amber-800 mb-1">
              Sourcing data could not be loaded
            </div>
            <div className="text-sm text-amber-700 mb-3">{loadError}</div>
            <button
              type="button"
              onClick={() => loadData(selectedRfpId || '')}
              className="px-3 py-2 rounded-lg border border-amber-400 text-amber-800 bg-white text-sm font-semibold"
            >
              Retry loading
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
            <ArrowRightLeft size={18} className="text-blue-600" />
            <h3 className="text-base font-semibold text-gray-800">
              Mapping Preview
            </h3>
          </div>

          {!mappingPreview ? (
            <div className="p-5 text-sm text-gray-500">
              Select a vendor response to preview conversion mapping.
            </div>
          ) : (
            <div className="p-5">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-3">Sourcing Field</th>
                      <th className="py-2 px-3">Mapped CLM Field</th>
                      <th className="py-2 pl-3">Preview Value</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-800">
                    <tr className="border-b">
                      <td className="py-2 pr-3">RFP + Vendor</td>
                      <td className="py-2 px-3">title</td>
                      <td className="py-2 pl-3">{mappingPreview.title}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-3">Selected Vendor</td>
                      <td className="py-2 px-3">counterparty</td>
                      <td className="py-2 pl-3">
                        {mappingPreview.counterparty}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-3">Bid Amount</td>
                      <td className="py-2 px-3">amount / currency</td>
                      <td className="py-2 pl-3">
                        {mappingPreview.amount.toLocaleString()}{' '}
                        {mappingPreview.currency}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-3">Contract Type</td>
                      <td className="py-2 px-3">type</td>
                      <td className="py-2 pl-3">{mappingPreview.type}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-3">Start/End Window</td>
                      <td className="py-2 px-3">startDate / endDate</td>
                      <td className="py-2 pl-3">
                        {mappingPreview.startDate} to {mappingPreview.endDate}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-3">Sourcing Source</td>
                      <td className="py-2 px-3">sourceRfpId / metadata</td>
                      <td className="py-2 pl-3">
                        {String(mappingPreview.sourceRfpId || '')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleCreateContract}
            disabled={!mappingPreview || creating}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <FilePlus2 size={16} />
            {creating ? 'Creating...' : 'Create Contract'}
          </button>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ContractConversion;
